import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { type ExperimentalFeatureKey, features } from '@luxuryescapes/contract-svc-promo';

import useCurrentUser from '~/hooks/useCurrentUser';
import { useUserPromoRoles } from '~/hooks/useUserPromoRoles';

import { promoRoles } from '~/services/PromoService';

import isProd from '~/utils/isProd';

import { findPromoFeatureIndicator } from './PromoTester/promoFeatureIndicators';
import { usePromoFeatureOptIn } from './PromoTester/usePromoFeatureOptIns';

interface Props {
  featureKey: ExperimentalFeatureKey;
}

function renderConfigChip({ icon, label, tooltip }: { icon?: React.ReactElement; label: string; tooltip?: string }) {
  const chip = <Chip icon={icon} label={label} size="small" />;

  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
}

/**
 * Shows a promo feature-registry entry to Promo-Beta-Tester users.
 *
 * The registry's `docType` determines whether an entry is presented as a
 * generally available feature or an experimental feature.
 */
export default function PromoFeatureConfigNotice({ featureKey }: Props) {
  const { user } = useCurrentUser();
  const { hasRole } = useUserPromoRoles({
    userId: user?.id_member ?? '',
    enabled: Boolean(user?.id_member),
  });
  const feature = findPromoFeatureIndicator(features, featureKey);
  const isPromoBetaTester = hasRole(promoRoles.PROMO_BETA_TESTER_ROLE);
  const { isOptedIn, optOut } = usePromoFeatureOptIn(
    user?.id_member ?? 'anonymous',
    featureKey,
    feature?.canOptIn ?? false,
  );

  if (!feature || !isPromoBetaTester) {
    return null;
  }

  const isExperimentalFeature = feature.docType !== 'feature';
  const isUnavailableTestFeature = feature.isOnlyTest && isProd();
  const presentationType = isExperimentalFeature ? 'Experimental feature' : 'Feature';

  return (
    <Alert severity={isUnavailableTestFeature ? 'warning' : 'info'} variant="outlined" sx={{ py: 0 }}>
      <Accordion disableGutters elevation={0} sx={{ backgroundColor: 'transparent' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              {presentationType} configuration – {feature.currentStatusHeadline}.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              More details
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <Stack spacing={1.5}>
            <Typography variant="body2">{feature.description}</Typography>
            {isOptedIn && (
              <Button variant="text" size="small" onClick={optOut} sx={{ alignSelf: 'flex-start' }}>
                Opt out
              </Button>
            )}
            {feature.notes && (
              <Typography variant="body2" color="text.secondary">
                {feature.notes}
              </Typography>
            )}
            <List dense disablePadding aria-label={`${feature.displayName} configuration details`}>
              <ListItem disableGutters>
                <ListItemText
                  primary="Configuration"
                  secondary={
                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
                      {renderConfigChip({ label: `Key: ${feature.key}` })}
                      {renderConfigChip({ label: `Category: ${feature.category}` })}
                      {renderConfigChip({
                        icon: <ScienceOutlinedIcon />,
                        label: isExperimentalFeature ? 'Experimental feature' : 'Feature',
                        tooltip: isExperimentalFeature
                          ? 'This entry is experimental and has not been promoted to a general feature.'
                          : 'This entry is documented as a general feature.',
                      })}
                      {feature.isOnlyTest &&
                        renderConfigChip({
                          icon: <WarningAmberOutlinedIcon />,
                          label: 'Test only',
                          tooltip: 'This configuration is unavailable in production.',
                        })}
                      {feature.isFunctionallyComplete &&
                        renderConfigChip({
                          icon: <CheckCircleOutlineIcon />,
                          label: 'Functionally complete',
                          tooltip: 'The configured capability is functionally complete.',
                        })}
                      {feature.requiredRole && renderConfigChip({ label: `Role: ${feature.requiredRole}` })}
                    </Stack>
                  }
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary="Status" secondary={feature.featureStatusHeadline} />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary="Current rollout" secondary={feature.currentStatusHeadline} />
              </ListItem>
            </List>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Alert>
  );
}
